import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, ArrowLeft, ImagePlus, Download, ExternalLink, ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ArticleModal({ isOpen, mode, onClose, onModeChange, onSave, onGenerateImage, articleData, onDelete }) {
  const [formData, setFormData] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (articleData) {
      setFormData(articleData);
      setCurrentImageIndex(0); // Reset image index when article changes
    }
  }, [articleData, mode]);

  if (!isOpen || !articleData) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUrlChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      publishStatus: value ? 'PUBLISHED' : 'UNPUBLISHED'
    }));
  };

  const handleSaveClick = () => {
    onSave(formData);
  };

  const handleDeleteClick = () => {
    if (window.confirm('确定要将该文章移至回收站吗？')) {
      onDelete(articleData.id);
      onClose();
    }
  };

  const getStatusPill = (status, type) => {
    if (!status) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">未知</span>;
    const styles = { audit: { APPROVED: "bg-green-100 text-green-700", PENDING: "bg-yellow-100 text-yellow-700", REJECTED: "bg-red-100 text-red-700" }, publish: { PUBLISHED: "bg-blue-100 text-blue-700", UNPUBLISHED: "bg-gray-100 text-gray-700" } };
    const text = { audit: { APPROVED: "采纳", PENDING: "待定", REJECTED: "废弃" }, publish: { PUBLISHED: "已发布", UNPUBLISHED: "未发布"} };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[type]?.[status]}`}>{text[type]?.[status] || '未知'}</span>;
  };

  const images = formData.images || [];
  const hasImages = images.length > 0;
  const currentImage = hasImages ? images[currentImageIndex] : null;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true);
      
      // Dynamically import to avoid top-level module errors
      const JSZip = (await import('jszip')).default || (await import('jszip'));
      const fileSaverModule = await import('file-saver');
      const saveAsFn = fileSaverModule.saveAs || fileSaverModule.default || fileSaverModule;

      const zip = new (JSZip.default ? JSZip.default : JSZip)();

      // 1. Add article content as txt file
      const articleText = `${formData.title}\n\n${formData.content || '无内容'}`;
      zip.file(`${formData.title || '文章'}.txt`, articleText);

      // 2. Add all associated images
      if (images && images.length > 0) {
        const imgFolder = zip.folder("images");
        const promises = images.map(async (img, index) => {
          if (!img.imageUrl) return;
          try {
            const url = img.imageUrl.startsWith('http') ? img.imageUrl : img.imageUrl;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            
            // Extract file extension or fallback to jpg
            const extMatch = url.match(/\.([^.?#]+)(?:[?#]|$)/);
            const ext = extMatch ? extMatch[1] : 'jpg';
            const filename = `image_${index + 1}.${ext}`;
            
            imgFolder.file(filename, blob);
          } catch (err) {
            console.error("Failed to fetch image for zip:", img.imageUrl, err);
          }
        });
        await Promise.all(promises);
      }

      // 3. Generate and download zip
      const content = await zip.generateAsync({ type: "blob" });
      saveAsFn(content, `${formData.title || '文章'}_打包.zip`);
    } catch (error) {
      console.error("Failed to generate zip:", error);
      alert("打包下载失败");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-0 pr-4">
            {mode === 'edit' && (
              <button 
                onClick={() => onModeChange('view')} 
                className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors mr-2 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-xl font-semibold text-gray-800 truncate" title={formData.title || '文章详情'}>
              {mode === 'edit' ? '编辑文章' : formData.title || '文章详情'}
            </h2>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {mode === 'view' && (
              <>
                <button 
                  onClick={handleDownloadAll}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <Download className="w-4 h-4 flex-shrink-0" />
                  {isDownloading ? '打包中...' : '打包下载'}
                </button>
                <button 
                  onClick={() => onModeChange('edit')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
                >
                  <Edit className="w-4 h-4 flex-shrink-0" />
                  修改内容
                </button>
                <button 
                  onClick={handleDeleteClick}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                >
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                  移入回收站
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-2 flex-shrink-0">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">
          <div className="max-w-4xl mx-auto bg-white border border-gray-100 rounded-xl shadow-sm p-8">
            {mode === 'view' ? (
              <div className="space-y-8">
                {/* Status Display */}
                <div className="flex items-center gap-4 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">审核状态:</span>
                    {getStatusPill(formData.auditStatus, 'audit')}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">发布状态:</span>
                    {getStatusPill(formData.publishStatus, 'publish')}
                  </div>
                  {formData.publishUrl && (
                    <div className="flex items-center gap-2 ml-auto">
                      <a href={formData.publishUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        查看已发布链接 <ExternalLink className="w-3 h-3"/>
                      </a>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Article Content */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-100 pb-2">文章正文</h3>
                    <div className="text-gray-700 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap font-sans text-sm leading-relaxed min-h-[300px] max-h-[600px] overflow-y-auto">
                      {formData.content || '暂无内容'}
                    </div>
                  </div>
                  
                  {/* Images & Info */}
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                        <h3 className="text-lg font-medium text-gray-900">关联图片 ({images.length})</h3>
                        <button onClick={() => onGenerateImage(formData)} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 font-medium bg-purple-50 px-3 py-1.5 rounded-md transition-colors">
                          <ImagePlus className="w-4 h-4"/> 生成新图
                        </button>
                      </div>

                      {hasImages ? (
                        <div className="space-y-3">
                          {/* Main Image Viewer */}
                          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200 group flex items-center justify-center">
                            <img 
                              src={currentImage.imageUrl} 
                              alt={`图片 ${currentImageIndex + 1}`} 
                              className="max-w-full max-h-full object-contain"
                            />
                            {images.length > 1 && (
                              <>
                                <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 hover:bg-black/60 transition-all">
                                  <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 hover:bg-black/60 transition-all">
                                  <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                                  {currentImageIndex + 1} / {images.length}
                                </div>
                              </>
                            )}
                          </div>
                          
                          {/* Image Thumbnails */}
                          {images.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto py-2 px-1">
                              {images.map((img, idx) => (
                                <button 
                                  key={idx} 
                                  onClick={() => setCurrentImageIndex(idx)}
                                  className={`relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${idx === currentImageIndex ? 'border-blue-500 shadow-sm' : 'border-transparent opacity-70 hover:opacity-100'}`}
                                >
                                  <img src={img.imageUrl && img.imageUrl.startsWith('http') ? img.imageUrl : `/api/thumb?url=${encodeURIComponent(img.imageUrl)}&w=150&h=150`} className="w-full h-full object-cover" alt="缩略图" loading="lazy" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full aspect-video rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400">
                          <ImageIcon className="w-10 h-10 mb-2 opacity-50" />
                          <span className="text-sm">暂无关联图片</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Column: Content Edit */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">文章标题</label>
                      <input 
                        type="text" 
                        name="title" 
                        value={formData.title || ''} 
                        onChange={handleChange} 
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">文章正文</label>
                      <textarea 
                        name="content"
                        rows={16}
                        value={formData.content || ''} 
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors text-sm leading-relaxed font-sans resize-none"
                      />
                    </div>
                  </div>

                  {/* Right Column: Status & Info Edit */}
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">审核状态</label>
                        <select
                          name="auditStatus"
                          value={formData.auditStatus || 'PENDING'}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                        >
                          <option value="PENDING">待定</option>
                          <option value="APPROVED">采纳</option>
                          <option value="REJECTED">废弃</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">发布状态</label>
                        <select
                          name="publishStatus"
                          value={formData.publishStatus || 'UNPUBLISHED'}
                          onChange={handleChange}
                          disabled
                          className="w-full px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                        >
                          <option value="UNPUBLISHED">未发布</option>
                          <option value="PUBLISHED">已发布</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">发布网址（填写后自动变为“已发布”）</label>
                      <input 
                        type="url" 
                        name="publishUrl"
                        value={formData.publishUrl || ''} 
                        onChange={handleUrlChange}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-colors"
                        placeholder="例如: https://mp.weixin.qq.com/s/..."
                      />
                    </div>
                    
                    {/* Basic Info Readonly */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 space-y-3 mt-4">
                      <h4 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">文章信息</h4>
                      <div className="text-xs text-gray-500 grid grid-cols-2 gap-2">
                         <span><b className="text-gray-600">ID:</b> {formData.id}</span>
                         <span><b className="text-gray-600">类型:</b> {formData.isSeed ? '优质文章' : '普通文章'}</span>
                         <span><b className="text-gray-600">创建:</b> {new Date(formData.createdAt).toLocaleString()}</span>
                         <span><b className="text-gray-600">更新:</b> {new Date(formData.updatedAt).toLocaleString()}</span>
                         <span><b className="text-gray-600">字数:</b> {(formData.content || '').length} 字</span>
                         <span><b className="text-gray-600">图片数:</b> {images.length} 张</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {mode === 'edit' && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white shrink-0">
            <button 
              onClick={() => onModeChange('view')} 
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              取消修改
            </button>
            <button 
              onClick={handleSaveClick} 
              className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
            >
              保存更改
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
